// FieldIQ — Broadcasts tab — announcements feed

import { useState, useEffect, useCallback } from "react";
import { Loader2, RefreshCw, Megaphone } from "lucide-react";
import { FIELDIQ_API_URL, getToken } from "../lib/api";

const BROADCAST_REACTIONS = ["👍", "✅", "❓", "❤️"];

function BroadcastCard({
  broadcast,
  myReactions,
  onReact,
  onRead,
  initiallyExpanded,
}) {
  const [expanded, setExpanded] = useState(!!initiallyExpanded);

  const handleExpand = () => {
    if (!expanded && !broadcast.is_read) onRead(broadcast.id);
    setExpanded((v) => !v);
  };

  const isPinned =
    broadcast.pinned_until && new Date(broadcast.pinned_until) > new Date();
  const isUrgent = broadcast.priority === "urgent";

  return (
    <div
      className={`bg-slate-800 border rounded-xl overflow-hidden transition-colors ${
        broadcast.is_read ? "border-slate-700" : "border-cyan-700"
      }`}
    >
      <button
        onClick={handleExpand}
        className="w-full text-left px-4 py-3 space-y-1"
      >
        <div className="flex items-center gap-2 flex-wrap">
          {!broadcast.is_read && (
            <span className="w-2 h-2 rounded-full bg-cyan-400 flex-shrink-0" />
          )}
          {isPinned && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 font-medium">
              📌 Pinned
            </span>
          )}
          {isUrgent && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-400 font-medium">
              ⚡ Urgent
            </span>
          )}
          <span className="text-xs text-slate-500 ml-auto">
            {broadcast.sender_name || "Leadership"} ·{" "}
            {new Date(broadcast.sent_at).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })}
          </span>
        </div>
        <p className="text-sm font-semibold text-white">{broadcast.title}</p>
        {!expanded && (
          <p className="text-xs text-slate-400 line-clamp-2">
            {broadcast.body}
          </p>
        )}
      </button>

      {expanded && (
        <div className="px-4 pb-3 space-y-3">
          <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">
            {broadcast.body}
          </p>
          {/* Reaction bar */}
          <div className="flex items-center gap-2 pt-2 border-t border-slate-700">
            {BROADCAST_REACTIONS.map((emoji) => {
              const count = broadcast.reactions?.[emoji] ?? 0;
              const mine = myReactions?.has(emoji);
              return (
                <button
                  key={emoji}
                  onClick={() => onReact(broadcast.id, emoji, mine)}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-sm transition-colors ${
                    mine
                      ? "bg-cyan-600/40 border border-cyan-500 text-white"
                      : "bg-slate-700 border border-slate-600 text-slate-300 hover:border-slate-500"
                  }`}
                >
                  {emoji}
                  {count > 0 && (
                    <span className="text-xs font-medium">{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export const BroadcastsTab = ({
  user,
  facility,
  isOnline,
  onNavigate,
  onUnreadChange,
}) => {
  const [broadcasts, setBroadcasts] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [loading, setLoading] = useState(true);
  // { broadcastId -> Set of emoji the current user reacted with }
  const [myReactions, setMyReactions] = useState({});

  const ldId = facility?.facility_id || "";

  const fetchBroadcasts = useCallback(
    async (silent = false) => {
      if (!ldId) return;
      if (!silent) setLoading(true);
      try {
        const res = await fetch(
          `${FIELDIQ_API_URL}/broadcasts?ld_id=${encodeURIComponent(ldId)}`,
          { headers: { Authorization: `Bearer ${getToken()}` } },
        );
        const data = res.ok ? await res.json() : null;
        if (data?.feed) {
          setBroadcasts(data.feed);
          setPinned(data.pinned || []);
          // Clear the nav badge — user has opened this tab
          onUnreadChange?.(0);
        }
      } catch {}
      setLoading(false);
    },
    [ldId], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    fetchBroadcasts();
  }, [fetchBroadcasts]);

  // Near-realtime: silently refresh the feed every 60s while visible
  useEffect(() => {
    const id = setInterval(() => {
      if (navigator.onLine && document.visibilityState === "visible") {
        fetchBroadcasts(true);
      }
    }, 60e3);
    return () => clearInterval(id);
  }, [fetchBroadcasts]);

  const handleRead = async (broadcastId) => {
    try {
      await fetch(`${FIELDIQ_API_URL}/broadcasts/${broadcastId}/read`, {
        method: "POST",
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      const mark = (list) =>
        list.map((b) => (b.id === broadcastId ? { ...b, is_read: true } : b));
      setBroadcasts(mark);
      setPinned(mark);
    } catch {}
  };

  const handleReact = async (broadcastId, emoji, alreadyReacted) => {
    const method = alreadyReacted ? "DELETE" : "POST";
    try {
      await fetch(`${FIELDIQ_API_URL}/broadcasts/${broadcastId}/react`, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getToken()}`,
        },
        body: JSON.stringify({ reaction: emoji }),
      });
      setMyReactions((prev) => {
        const s = new Set(prev[broadcastId] || []);
        alreadyReacted ? s.delete(emoji) : s.add(emoji);
        return { ...prev, [broadcastId]: s };
      });
      // Optimistically update reaction count
      const update = (list) =>
        list.map((b) => {
          if (b.id !== broadcastId) return b;
          const r = { ...b.reactions };
          r[emoji] = Math.max(0, (r[emoji] ?? 0) + (alreadyReacted ? -1 : 1));
          return { ...b, reactions: r };
        });
      setBroadcasts(update);
      setPinned(update);
    } catch {}
  };

  const all = [...pinned, ...broadcasts];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-white">Announcements</h2>
          <p className="text-xs text-slate-400">
            From {facility?.display_name || "LD Leadership"} · read-only
          </p>
        </div>
        <button
          onClick={() => fetchBroadcasts()}
          disabled={loading || !isOnline}
          className="p-2 text-slate-500 hover:text-slate-300"
        >
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12 text-slate-500">
          <Loader2 size={22} className="animate-spin" />
        </div>
      ) : all.length === 0 ? (
        <div className="text-center py-14 border-2 border-dashed border-slate-700 rounded-xl">
          <Megaphone size={28} className="mx-auto mb-3 text-slate-600" />
          <p className="text-sm text-slate-400">No announcements yet.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {all.map((b) => (
            <BroadcastCard
              key={b.id}
              broadcast={b}
              myReactions={myReactions[b.id]}
              onReact={handleReact}
              onRead={handleRead}
              initiallyExpanded={false}
            />
          ))}
        </div>
      )}
    </div>
  );
};
